import React, { useState, useEffect } from 'react';
import './ConcertTicket.css'; 

// Ticket System Configuration
const ticketConfig = {
    systemName: "EventTick Pro",
    currencySymbol: "KSh"
};

const HomePage = () => {
    // State management
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successReceipt, setSuccessReceipt] = useState('');
    const [processing, setProcessing] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState('waiting');
    const [checkoutRequestId, setCheckoutRequestId] = useState('');
    
    // Form state
    const [formData, setFormData] = useState({
        customerName: '',
        customerEmail: '',
        customerPhone: ''
    });

    // Firebase config
    const firebaseConfig = {
        apiKey: "AIzaSyB_mbOl5VyHwM7jitYbpTJkRelbFyZVxD8",
        authDomain: "white-doves-ministry.firebaseapp.com",
        databaseURL: "https://white-doves-ministry-default-rtdb.firebaseio.com",
        projectId: "white-doves-ministry",
        storageBucket: "white-doves-ministry.firebasestorage.app",
        messagingSenderId: "1055532339208",
        appId: "1:1055532339208:web:09f8ab4e5a79c0f73c01bd"
    };

    // Initialize Firebase
    useEffect(() => {
        // Load Firebase script dynamically
        const loadFirebase = async () => {
            if (!window.firebase) {
                const script = document.createElement('script');
                script.src = 'https://www.gstatic.com/firebasejs/10.6.0/firebase-app-compat.js';
                document.head.appendChild(script);
                
                await new Promise(resolve => {
                    script.onload = resolve;
                });
                
                const authScript = document.createElement('script');
                authScript.src = 'https://www.gstatic.com/firebasejs/10.6.0/firebase-auth-compat.js';
                document.head.appendChild(authScript);
                
                const firestoreScript = document.createElement('script');
                firestoreScript.src = 'https://www.gstatic.com/firebasejs/10.6.0/firebase-firestore-compat.js';
                document.head.appendChild(firestoreScript);
                
                await new Promise(resolve => {
                    firestoreScript.onload = resolve;
                });
                
                window.firebase.initializeApp(firebaseConfig);
            }
        };
        
        loadFirebase();
        loadEvents();
    }, []);

    // Load events from Firestore
    const loadEvents = async () => {
        setLoading(true);
        try {
            const now = new Date();
            const snapshot = await window.firebase.firestore()
                .collection('concerts')
                .where('eventDate', '>=', window.firebase.firestore.Timestamp.fromDate(now))
                .orderBy('eventDate', 'asc')
                .get();

            const eventsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            setEvents(eventsData);
            setError(null);
        } catch (err) {
            console.error('Error loading events:', err);
            setError('Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    // Format date from Firestore timestamp
    const formatEventDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return timestamp.toDate().toLocaleString();
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id.replace('customer', '').toLowerCase()]: value
        }));
    };

    // Phone number formatting
    const handlePhoneChange = (e) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 0 && !value.startsWith('254')) {
            value = '254' + value;
        }
        setFormData(prev => ({ ...prev, phone: value }));
    };

    // Open purchase modal
    const openPurchaseModal = (event, ticket) => {
        setSelectedEvent({ event, ticket });
        setShowPurchaseModal(true);
    };

    // Close all modals
    const closeModals = () => {
        setShowPurchaseModal(false);
        setShowPaymentModal(false);
        setShowSuccessModal(false);
        setShowErrorModal(false);
        setFormData({ customerName: '', customerEmail: '', customerPhone: '' });
        setProcessing(false);
    };

    // Handle purchase
    const handlePurchase = async () => {
        const { customerName, customerEmail, customerPhone } = formData;
        
        if (!customerName || !customerEmail || !customerPhone) {
            showError('Please fill all fields');
            return;
        }

        if (!customerPhone.match(/^254[0-9]{9}$/)) {
            showError('Please enter a valid phone number (e.g., 2547XXXXXXXX)');
            return;
        }

        if (!customerEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            showError('Please enter a valid email address');
            return;
        }

        setProcessing(true);
        
        try {
            const orderId = 'ORD-' + Date.now() + '-' + Math.random().toString(36).substr(2, 5).toUpperCase();
            
            const response = await fetch('/.netlify/functions/stk-push', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    phone: customerPhone,
                    amount: parseFloat(selectedEvent.ticket.price),
                    orderId: orderId,
                    eventId: selectedEvent.event.id,
                    ticketType: selectedEvent.ticket.name,
                    customerName: customerName,
                    customerEmail: customerEmail
                })
            });

            const data = await response.json();

            if (data.success) {
                setShowPurchaseModal(false);
                setCheckoutRequestId(data.data.checkoutRequestId);
                setShowPaymentModal(true);
                setFormData({ customerName: '', customerEmail: '', customerPhone: '' });
                startPaymentPolling(data.data.checkoutRequestId);
            } else {
                showError(data.error || 'Payment initiation failed');
            }
        } catch (err) {
            console.error('Error:', err);
            showError('Network error. Please check your connection.');
        } finally {
            setProcessing(false);
        }
    };

    // Poll payment status
    const startPaymentPolling = (checkoutRequestId) => {
        const maxAttempts = 30;
        let attempts = 0;
        
        const interval = setInterval(async () => {
            attempts++;
            
            try {
                const response = await fetch(`/.netlify/functions/mpesa-status?checkoutRequestId=${checkoutRequestId}`);
                const data = await response.json();
                
                if (data.status === 'completed') {
                    clearInterval(interval);
                    setShowPaymentModal(false);
                    setSuccessReceipt(data.mpesaReceipt);
                    setShowSuccessModal(true);
                    
                } else if (data.status === 'failed') {
                    clearInterval(interval);
                    setShowPaymentModal(false);
                    showError(data.resultDesc || 'Transaction failed');
                    
                } else if (attempts >= maxAttempts) {
                    clearInterval(interval);
                    setPaymentStatus('timeout');
                }
            } catch (err) {
                console.error('Status poll error:', err);
            }
        }, 4000);
    };

    // Show error modal
    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorModal(true);
        setTimeout(() => setShowErrorModal(false), 5000);
    };

    // Countdown component
    const CountdownTimer = ({ eventDate, eventId }) => {
        const [timeLeft, setTimeLeft] = useState('');
        const [isActive, setIsActive] = useState(true);

        useEffect(() => {
            const calculateTimeLeft = () => {
                const now = new Date();
                const eventTime = eventDate.toDate();
                const diff = eventTime - now;

                if (diff <= 0) {
                    setIsActive(false);
                    return 'Event Ended';
                }

                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);

                return `${days}d ${hours}h ${minutes}m ${seconds}s`;
            };

            setTimeLeft(calculateTimeLeft());

            const timer = setInterval(() => {
                setTimeLeft(calculateTimeLeft());
            }, 1000);

            return () => clearInterval(timer);
        }, [eventDate]);

        if (!isActive) {
            return <div className="countdown-timer ended"><i className="fas fa-calendar-times"></i> Event Ended</div>;
        }

        return (
            <div className="countdown-timer active">
                <i className="fas fa-hourglass-half"></i> Event starts in: {timeLeft}
            </div>
        );
    };

    // Event Card Component
    const EventCard = ({ event }) => {
        const eventDate = event.eventDate.toDate();
        const now = new Date();
        const isActive = eventDate > now;

        return (
            <div className="event-card animate__animated animate__fadeInUp">
                <img 
                    src={event.imageUrl || 'https://via.placeholder.com/300x200?text=Event'} 
                    alt={event.name} 
                    className="event-image"
                    onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Event'}
                />
                <div className="event-details">
                    <h3><i className="fas fa-calendar-alt"></i> {event.name}</h3>
                    <p><i className="fas fa-align-left"></i> {event.description || 'No description available'}</p>
                    <p><i className="fas fa-calendar-day"></i> <strong>Date:</strong> {eventDate.toLocaleDateString()} at {eventDate.toLocaleTimeString()}</p>
                    <p><i className="fas fa-map-marker-alt"></i> <strong>Venue:</strong> {event.venue || 'TBA'}</p>
                    
                    {isActive && <CountdownTimer eventDate={event.eventDate} eventId={event.id} />}
                    
                    <div className="ticket-types">
                        {event.ticketTypes?.map((ticket, index) => (
                            <div key={index} className="ticket-type">
                                <span><i className="fas fa-tag"></i> {ticket.name}</span>
                                <span>{ticketConfig.currencySymbol} {ticket.price.toLocaleString()}</span>
                                <button 
                                    className="buy-btn" 
                                    disabled={!isActive}
                                    onClick={() => openPurchaseModal(event, ticket)}
                                >
                                    <i className="fas fa-shopping-cart"></i> Buy
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // Loading skeleton
    const LoadingSkeleton = () => (
        <>
            {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="event-card skeleton" style={{ height: '400px' }}></div>
            ))}
        </>
    );

    return (
        <div className="homepage">
            {/* Header */}
            <header className="header animate__animated animate__slideInDown">
                <img src={ticketConfig.systemLogo} alt="Logo" className="logo" />
                <h1 className="system-name">{ticketConfig.systemName}</h1>
                <nav className="nav-links">
                    <a href="/"><i className="fas fa-home"></i> Home</a>
                    <a href="/admin.html"><i className="fas fa-user-shield"></i> Admin</a>
                </nav>
            </header>

            {/* Main Content */}
            <main>
                <h2 className="page-title animate__animated animate__fadeInUp">
                    <i className="fas fa-calendar-alt"></i> Upcoming Events
                </h2>

                <div className="event-grid">
                    {loading && <LoadingSkeleton />}
                    
                    {error && (
                        <div className="error-container">
                            <i className="fas fa-exclamation-triangle"></i>
                            <h3>{error}</h3>
                            <p>Please try again later.</p>
                        </div>
                    )}

                    {!loading && !error && events.length === 0 && (
                        <div className="empty-container">
                            <i className="fas fa-calendar-times"></i>
                            <h3>No upcoming events</h3>
                            <p>Check back later for exciting events!</p>
                        </div>
                    )}

                    {!loading && !error && events.map(event => (
                        <EventCard key={event.id} event={event} />
                    ))}
                </div>
            </main>

            {/* Purchase Modal */}
            {showPurchaseModal && (
                <div className="modal" onClick={(e) => e.target.className === 'modal' && closeModals()}>
                    <div className="modal-content animate__animated animate__slideInUp">
                        <span className="close" onClick={closeModals}>&times;</span>
                        <h2><i className="fas fa-ticket-alt"></i> Purchase Ticket</h2>
                        
                        {selectedEvent && (
                            <div className="selected-ticket">
                                <div className="ticket-type" style={{ marginBottom: '20px' }}>
                                    <span><i className="fas fa-ticket-alt"></i> {selectedEvent.ticket.name}</span>
                                    <span style={{ fontSize: '1.3rem' }}>
                                        {ticketConfig.currencySymbol} {selectedEvent.ticket.price.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="mpesa-icon">
                            <i className="fas fa-mobile-alt"></i>
                            <span>Pay with M-PESA</span>
                            <i className="fas fa-lock"></i>
                        </div>

                        <input 
                            type="text" 
                            id="customerName" 
                            placeholder="Full Name" 
                            value={formData.name}
                            onChange={handleInputChange}
                            disabled={processing}
                            required
                        />
                        
                        <input 
                            type="email" 
                            id="customerEmail" 
                            placeholder="Email Address" 
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled={processing}
                            required
                        />
                        
                        <input 
                            type="tel" 
                            id="customerPhone" 
                            placeholder="Phone Number (e.g., 2547XXXXXXXX)" 
                            value={formData.phone}
                            onChange={handlePhoneChange}
                            disabled={processing}
                            required
                        />

                        <button 
                            className="modal-btn purchase-btn" 
                            onClick={handlePurchase}
                            disabled={processing}
                        >
                            {processing ? (
                                <><i className="fas fa-spinner fa-spin"></i> Processing...</>
                            ) : (
                                <><i className="fas fa-shopping-cart"></i> Purchase Ticket</>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Payment Status Modal */}
            {showPaymentModal && (
                <div className="modal" onClick={(e) => e.target.className === 'modal' && closeModals()}>
                    <div className="modal-content animate__animated animate__slideInUp">
                        <h3><i className="fas fa-credit-card"></i> Payment Status</h3>
                        
                        <div className="mpesa-icon">
                            <i className="fas fa-mobile-alt"></i>
                            <span>M-PESA Transaction</span>
                        </div>

                        <p>Please check your phone and enter your M-PESA PIN to complete the payment.</p>
                        
                        <div className="spinner"></div>
                        
                        <p className="status-message">
                            {paymentStatus === 'waiting' && 'Waiting for payment...'}
                            {paymentStatus === 'timeout' && 'Payment taking longer than expected. Please check your M-PESA messages.'}
                        </p>

                        <div className="modal-buttons">
                            <button className="modal-btn" onClick={() => setShowPaymentModal(false)}>
                                <i className="fas fa-times"></i> Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="modal" onClick={(e) => e.target.className === 'modal' && closeModals()}>
                    <div className="modal-content animate__animated animate__slideInUp">
                        <div className="success-message">
                            <i className="fas fa-check-circle"></i>
                            <h3>Payment Successful!</h3>
                            <p>Your ticket has been booked successfully.</p>
                            <p className="receipt">
                                <i className="fas fa-receipt"></i> Receipt: {successReceipt}
                            </p>
                        </div>

                        <button className="modal-btn" onClick={closeModals}>
                            <i className="fas fa-home"></i> Done
                        </button>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            {showErrorModal && (
                <div className="modal" onClick={(e) => e.target.className === 'modal' && closeModals()}>
                    <div className="modal-content animate__animated animate__slideInUp">
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            <h3>Payment Failed</h3>
                            <p>{errorMessage}</p>
                        </div>

                        <button className="modal-btn" onClick={() => setShowErrorModal(false)}>
                            <i className="fas fa-times"></i> Close
                        </button>
                    </div>
                </div>
            )}

            {/* Footer */}
            <footer className="footer">
                <i className="fas fa-copyright"></i> {new Date().getFullYear()} EventTick Pro. All rights reserved.
                <p>
                    <i className="fas fa-envelope"></i> support@eventickpro.com | 
                    <i className="fas fa-phone"></i> +254 700 000 000
                </p>
            </footer>
        </div>
    );
};

export default HomePage;
